import { useContext, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { Close as CloseIcon, Casino as CasinoIcon } from "@mui/icons-material";
import { Emoji } from "emoji-picker-react";
import { ActivitiesContext } from "../../context/activities/ActivitiesContext";
import { DEFAULT_ACTIVITY_COLOR } from "../../constants/pickerColors";

const RandomActivitySelector = () => {
  const {
    activities,
    randomDialogOpen,
    closeRandomSelector,
    isSpinning,
    currentActivityIndex,
    selectedActivity,
    spinSpeed,
    startSpin,
  } = useContext(ActivitiesContext);

  const [showConfetti, setShowConfetti] = useState(false);
  const [singleActivityRevealed, setSingleActivityRevealed] = useState(false);

  const currentActivity = activities[currentActivityIndex];
  const hasRolled = isSpinning || selectedActivity !== null;
  const isSingleActivity = activities.length === 1;

  // Trigger confetti when spin completes
  useEffect(() => {
    if (selectedActivity && !isSpinning) {
      setShowConfetti(true);
    }
  }, [selectedActivity, isSpinning]);

  // Reset single activity state when dialog closes
  useEffect(() => {
    if (!randomDialogOpen) {
      setSingleActivityRevealed(false);
      setShowConfetti(false);
    }
  }, [randomDialogOpen]);

  // Handle single activity reveal
  const handleSingleActivityRoll = () => {
    setSingleActivityRevealed(true);
    setShowConfetti(true);
  };

  return (
    <Dialog
      open={randomDialogOpen}
      onClose={closeRandomSelector}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{ color: "#004a3c", display: "flex", alignItems: "center", gap: 1 }}
      >
        Random Activity
        <IconButton
          onClick={closeRandomSelector}
          sx={{ ml: "auto" }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflow: "hidden", p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 2,
          }}
        >
          {/* Activity card slider */}
          <Box
            className={`${(selectedActivity && !isSpinning && showConfetti) || singleActivityRevealed ? "confetti-celebration confetti-celebration--active" : ""}`}
            sx={{
              width: "100%",
              position: "relative",
              minHeight: "80px",
              overflow: "visible",
              p: 3,
            }}
          >
            {isSingleActivity ? (
              // Single activity case - show immediately with sarcasm
              singleActivityRevealed ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    px: 1,
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      boxSizing: "border-box",
                      border: "1px solid #d0d0d0",
                      borderLeft: `4px solid ${activities[0].color || DEFAULT_ACTIVITY_COLOR}`,
                      boxShadow: 6,
                      transform: "scale(1.02)",
                      animation: "slideIn 0.3s ease-out",
                      "@keyframes slideIn": {
                        "0%": {
                          transform: "translateX(50%)",
                          opacity: 0,
                        },
                        "100%": {
                          transform: "translateX(0)",
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 1.5,
                        "&:last-child": { pb: 1.5 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          bgcolor:
                            activities[0].color || DEFAULT_ACTIVITY_COLOR,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <Emoji
                          unified={activities[0].emoji || "1f4cb"}
                          size={28}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {activities[0].name}
                        </Typography>
                        {activities[0].description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {activities[0].description}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      color: "#666",
                      fontStyle: "italic",
                      textAlign: "center",
                      animation: "fadeIn 0.5s ease-out 0.3s both",
                      "@keyframes fadeIn": {
                        "0%": { opacity: 0 },
                        "100%": { opacity: 1 },
                      },
                    }}
                  >
                    ...what did you think was going to happen here?
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "64px !important",
                      textAlign: "center",
                      animation: "fadeIn 0.5s ease-out 0.3s both",
                    }}
                  >
                    😗
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #cccccc",
                    borderRadius: "4px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: "#999", textAlign: "center", px: 2 }}
                  >
                    Click "Roll Activity" to pick a random activity
                  </Typography>
                </Box>
              )
            ) : !hasRolled ? (
              <Box
                sx={{
                  width: "100%",
                  height: "80px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px dashed #cccccc",
                  borderRadius: "4px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#999", textAlign: "center", px: 2 }}
                >
                  Click "Roll Activity" to pick a random activity
                </Typography>
              </Box>
            ) : (
              activities.length > 0 &&
              currentActivity && (
                <Card
                  key={currentActivityIndex}
                  sx={{
                    border: "1px solid #d0d0d0",
                    borderLeft: `4px solid ${currentActivity.color || DEFAULT_ACTIVITY_COLOR}`,
                    boxShadow: selectedActivity ? 6 : 2,
                    transform: selectedActivity ? "scale(1.02)" : "scale(1)",
                    transition: selectedActivity ? "all 0.3s ease" : "none",
                    animation: isSpinning
                      ? `slideInOut ${spinSpeed / 1000}s ease-in-out`
                      : selectedActivity
                        ? "slideIn 0.3s ease-out"
                        : "none",
                    "@keyframes slideInOut": {
                      "0%": {
                        transform: "translateX(100%)",
                        opacity: 0,
                      },
                      "20%": {
                        opacity: 1,
                      },
                      "50%": {
                        transform: "translateX(0)",
                        opacity: 1,
                      },
                      "80%": {
                        opacity: 1,
                      },
                      "100%": {
                        transform: "translateX(-100%)",
                        opacity: 0,
                      },
                    },
                    "@keyframes slideIn": {
                      "0%": {
                        transform: "translateX(50%)",
                        opacity: 0,
                      },
                      "100%": {
                        transform: "translateX(0)",
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1.5,
                      "&:last-child": { pb: 1.5 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor:
                          currentActivity.color || DEFAULT_ACTIVITY_COLOR,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <Emoji
                        unified={currentActivity.emoji || "1f4cb"}
                        size={28}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {currentActivity.name}
                      </Typography>
                      {currentActivity.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {currentActivity.description}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        {/* Hide button entirely after single activity is revealed */}
        {!(isSingleActivity && singleActivityRevealed) && (
          <Box
            sx={{
              position: "relative",
              borderRadius: 1,
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, #197b68 0, #276580 400px, #6a205c 800px, #276580 1200px, #197b68 1600px)",
                backgroundSize: "1600px 100%",
                backgroundRepeat: "repeat-x",
                backgroundPosition: "0 0",
                animation: "gradient-flow 6s linear infinite",
              },
              "@keyframes gradient-flow": {
                from: {
                  backgroundPositionX: "0",
                },
                to: {
                  backgroundPositionX: "-1600px",
                },
              },
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={isSingleActivity ? handleSingleActivityRoll : startSpin}
              disabled={isSpinning}
              startIcon={<CasinoIcon />}
              sx={{
                px: 4,
                backgroundColor: "transparent",
                color: "white",
                position: "relative",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                },
                "&:disabled": {
                  backgroundColor: "transparent",
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "&.MuiButton-root": {
                  boxShadow: "none",
                },
              }}
            >
              {isSpinning
                ? "Rolling..."
                : selectedActivity
                  ? "Roll Again"
                  : "Roll Activity"}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RandomActivitySelector;
